import type { Search_RequestBody } from "@opensearch-project/opensearch/api";
import type { ResponseBody } from "@opensearch-project/opensearch/api/_types/_core.search";

function resolveOpenSearchPostUrl(): string {
    const raw = process.env.REACT_APP_OPEN_SEARCH_ENDPOINT;
    if (!raw) {
        return "";
    }
    const trimmed = raw.replace(/\/+$/, "");
    return trimmed.endsWith("/product/search")
        ? trimmed
        : `${trimmed}/product/search`;
}

export type ProductSearchServiceOptions = {
    /** Sent as `store-id` when the index expects dispensary short name scope. */
    storeShortName?: string;
};

/**
 * OpenSearch product search (CRA env: `REACT_APP_*`).
 */
export class ProductSearchService {
    private readonly _entityId: string;

    private readonly _orgId: string;

    private readonly _apiKey: string;

    constructor(
        entityId: string,
        orgId: string,
        options?: ProductSearchServiceOptions,
    ) {
        this._entityId = entityId;
        this._orgId = orgId;
        this._apiKey = process.env.REACT_APP_OPEN_SEARCH_API_KEY ?? "";
    }

    search = async ({
        searchBody,
    }: {
        searchBody: Search_RequestBody;
    }): Promise<ResponseBody> => {
        const url = resolveOpenSearchPostUrl();
        if (!url) {
            throw new Error(
                "ProductSearchService: REACT_APP_OPEN_SEARCH_ENDPOINT is not set",
            );
        }

        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "entity-id": this._entityId,
            "org-id": this._orgId,
            "X-Api-Key": this._apiKey,
        };

        const response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify(searchBody),
            cache: "no-store",
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            let json: unknown = {};
            try {
                json = text ? JSON.parse(text) : {};
            } catch {
                json = { raw: text };
            }
            const err = new Error(
                "ProductSearchService: request failed",
            ) as Error & {
                cause?: { status: number; statusText: string; json: unknown };
            };
            err.cause = {
                status: response.status,
                statusText: response.statusText,
                json,
            };
            throw err;
        }

        return response.json() as Promise<ResponseBody>;
    };
}
